import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiPlus, FiRefreshCw, FiTrash2, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './addSales.css';

const AddSales = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [salesItems, setSalesItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    productId: '',
    quantity: 1,
    unit: 'piece',
    sellingPrice: '',
    calculatedPrice: '',
    productSearch: '',
    showDropdown: false
  });
  const [formData, setFormData] = useState({
    retailerId: '',
    saleDate: new Date().toISOString().split('T')[0],
    paymentStatus: 'paid',
    paidAmount: 0,
    paymentType: 'cash',
    bankAccountId: '',
    receiptNo: '',
    customerName: '',
    discountType: 'amount',
    discountValue: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const searchRef = useRef(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, retailersRes, bankAccountsRes] = await Promise.all([
          axios.get('/items/all'),
          axios.get('/retailers'),
          axios.get('/bank-accounts')
        ]);

        setProducts(productsRes.data.data.map(item => ({
          id: item.item_id,
          name: item.item_name,
          costPrice: item.cost_price,
          sellingPrice: item.selling_price,
          stock: item.quantity,
          unit: item.unit || 'piece'
        })));

        setRetailers(retailersRes.data.map(retailer => ({
          id: retailer.retailer_id,
          name: retailer.name
        })));

        setBankAccounts(Array.isArray(bankAccountsRes.data) ? bankAccountsRes.data : []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setApiError('Failed to load products, retailers, and bank accounts');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setCurrentItem(prev => ({ ...prev, showDropdown: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProducts = products.filter(product => {
    const searchTerm = currentItem.productSearch.toLowerCase();
    return product.name.toLowerCase().includes(searchTerm) ||
           product.id.toString().includes(searchTerm);
  }).sort((a, b) => {
    const aStartsWith = a.name.toLowerCase().startsWith(currentItem.productSearch.toLowerCase());
    const bStartsWith = b.name.toLowerCase().startsWith(currentItem.productSearch.toLowerCase());
    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    return 0;
  });

  const [refreshingProducts, setRefreshingProducts] = useState(false);

  const refreshProducts = async () => {
    setRefreshingProducts(true);
    try {
      const productsRes = await axios.get('/items/');
      setProducts(productsRes.data.data.map(item => ({
        id: item.item_id,
        name: item.item_name,
        costPrice: item.cost_price,
        sellingPrice: item.selling_price,
        stock: item.quantity,
        unit: item.unit || 'piece'
      })));
    } catch (err) {
      console.error('Error refreshing products:', err);
    } finally {
      setRefreshingProducts(false);
    }
  };

  const handleCurrentItemChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: value,
      showDropdown: name === 'productSearch' && value.length > 0
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'paymentType' && value !== 'bank' ? { bankAccountId: '' } : {})
    }));
  };

  const selectProduct = (product) => {
    if (product.stock <= 0) return;

    const basePrice = Number(product.sellingPrice) || 0;
    let unit = product.unit || 'piece';
    let calculatedPrice = basePrice;

    if (unit === 'set') {
      calculatedPrice = basePrice * 6;
    } else if (unit === 'dozen') {
      calculatedPrice = basePrice * 12;
    }

    setCurrentItem({
      productId: product.id,
      productSearch: `${product.name} `,
      quantity: 1,
      unit: unit,
      sellingPrice: basePrice,
      calculatedPrice: calculatedPrice,
      showDropdown: false
    });
  };

  const getUnitOptions = (productUnit) => {
    if (productUnit === 'piece') return ['piece'];
    if (productUnit === 'kg') return ['kg'];
    if (productUnit === 'set') return ['set', 'piece'];
    if (productUnit === 'dozen') return ['dozen', 'piece'];
    return ['piece'];
  };

  const handleUnitChange = (e) => {
    const newUnit = e.target.value;
    const product = products.find(p => p.id === currentItem.productId);
    const basePrice = currentItem.sellingPrice || product?.sellingPrice || 0;

    let calculatedPrice = basePrice;
    if (newUnit === 'set') calculatedPrice = basePrice * 6;
    else if (newUnit === 'dozen') calculatedPrice = basePrice * 12;

    setCurrentItem(prev => ({
      ...prev,
      unit: newUnit,
      calculatedPrice: calculatedPrice
    }));
  };

  const addSalesItem = () => {
    const product = products.find(p => p.id === currentItem.productId);
    if (!product) return;

    const multiplier = currentItem.unit === 'set' ? 6 :
                     currentItem.unit === 'dozen' ? 12 : 1;
    const actualQuantity = currentItem.quantity * multiplier;

    const newItem = {
      item_id: currentItem.productId,
      item_name: product.name,
      quantity: currentItem.quantity,
      unit: currentItem.unit,
      actualQuantity,
      selling_price: parseFloat(currentItem.sellingPrice),
      calculated_price: parseFloat(currentItem.calculatedPrice),
      cost_price: product.costPrice
    };

    setSalesItems([...salesItems, newItem]);
    setCurrentItem({
      productId: '',
      quantity: 1,
      unit: 'piece',
      sellingPrice: '',
      calculatedPrice: '',
      productSearch: '',
      showDropdown: false
    });
  };

  const removeSalesItem = (index) => {
    const newItems = [...salesItems];
    newItems.splice(index, 1);
    setSalesItems(newItems);
  };

  const calculateTotal = () => {
    return salesItems.reduce((sum, item) => {
      const multiplier = item.unit === 'set' ? 6 : item.unit === 'dozen' ? 12 : 1;
      return sum + (item.selling_price * item.quantity * multiplier);
    }, 0);
  };

  const calculateDiscountAmount = () => {
    const subtotal = calculateTotal();
    const value = parseFloat(formData.discountValue) || 0;
    const raw = formData.discountType === 'percentage'
      ? subtotal * value / 100
      : value;
    const clamped = Math.min(Math.max(raw, 0), subtotal);
    return Math.round(clamped * 100) / 100;
  };

  const calculateNetTotal = () => {
    return calculateTotal() - calculateDiscountAmount();
  };

  const validateForm = () => {
    const newErrors = {};

    if (salesItems.length === 0) {
      newErrors.salesItems = 'At least one product is required';
    }

    if (formData.retailerId) {
      if (parseFloat(formData.paidAmount) < 0) {
        newErrors.paidAmount = 'Paid amount cannot be negative';
      } else if (parseFloat(formData.paidAmount) > calculateNetTotal()) {
        newErrors.paidAmount = 'Paid amount cannot exceed total amount';
      }
    } else {
      if (formData.customerName.trim()) {
        if (formData.paidAmount === '' || isNaN(formData.paidAmount)) {
          newErrors.paidAmount = 'Paid amount is required';
        } else if (parseFloat(formData.paidAmount) < 0) {
          newErrors.paidAmount = 'Paid amount cannot be negative';
        } else if (parseFloat(formData.paidAmount) > calculateNetTotal()) {
          newErrors.paidAmount = 'Paid amount cannot exceed total amount';
        }
      } else {
        if (!formData.paidAmount || isNaN(formData.paidAmount)) {
          newErrors.paidAmount = 'Paid amount is required';
        } else if (parseFloat(formData.paidAmount) <= 0) {
          newErrors.paidAmount = 'Paid amount must be greater than 0';
        } else if (parseFloat(formData.paidAmount) > calculateNetTotal()) {
          newErrors.paidAmount = 'Paid amount cannot exceed total amount';
        }

        if (calculateNetTotal() > parseFloat(formData.paidAmount || 0)) {
          if (!formData.customerName.trim()) {
            newErrors.customerName = 'Customer name is required for credit sales';
          }
        }
      }
    }

    if (formData.paymentType === 'bank' && parseFloat(formData.paidAmount || 0) > 0 && !formData.bankAccountId) {
      newErrors.bankAccountId = 'Please select a bank account';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const saleData = {
        retailer_id: formData.retailerId || null,
        sale_date: formData.saleDate,
        paid_amount: parseFloat(formData.paidAmount),
        payment_type: formData.paymentType,
        bank_account_id: formData.paymentType === 'bank' && parseFloat(formData.paidAmount || 0) > 0
          ? parseInt(formData.bankAccountId)
          : null,
        receipt_no: formData.receiptNo || null,
        customer_name: formData.customerName || null,
        discount_type: formData.discountType,
        discount_value: parseFloat(formData.discountValue) || 0,
        items: salesItems.map(item => ({
          item_id: parseInt(item.item_id),
          quantity: parseInt(item.quantity),
          selling_price: parseFloat(item.selling_price),
          unit: item.unit
        }))
      };

      const response = await axios.post('/sales', saleData);
      const invoiceResponse = await axios.get(`/sales/invoice/${response.data.invoice_id}`);
      setInvoicePreview(invoiceResponse.data);
      setShowInvoiceModal(true);

    } catch (err) {
      console.error('Error:', err.response?.data || err);
      if (err.response?.data?.errors) {
        const backendErrors = {};
        err.response.data.errors.forEach(error => {
          backendErrors[error.param] = error.msg;
        });
        setErrors(backendErrors);
      }
      setApiError(err.response?.data?.error || 'Failed to record sale');
    } finally {
      setIsSubmitting(false);
    }
  };

  const lineTotal = (item) => {
    const multiplier = item.unit === 'set' ? 6 : item.unit === 'dozen' ? 12 : 1;
    return item.selling_price * item.quantity * multiplier;
  };

  const normalizedUnitPrice = (item) => {
    const multiplier = item.unit === 'set' ? 6 : item.unit === 'dozen' ? 12 : 1;
    return item.selling_price * multiplier;
  };

  const fmt = (n) => (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const subtotal = calculateTotal();
  const discount = calculateDiscountAmount();
  const netTotal = calculateNetTotal();
  const paid = parseFloat(formData.paidAmount || 0);
  const due = Math.max(0, netTotal - paid);

  if (apiError) return <div className="error">Error: {apiError}</div>;

  return (
    <div className="add-sales-page">
      <div className="asp-topbar">
        <div className="asp-topbar-title">
          <span className="asp-eyebrow">Shasha Kitchen · Till</span>
          <h1 className="asp-title">Record a sale</h1>
        </div>
        <button
          className="asp-cancel-btn"
          onClick={() => navigate('/sales')}
          disabled={isSubmitting}
        >
          <FiX /> Cancel
        </button>
      </div>

      {apiError && <div className="asp-api-error">{apiError}</div>}

      <form onSubmit={handleSubmit} className="asp-workspace">
        <div className="asp-form-column">

          {/* Product search + add */}
          <div className="asp-card">
            <div className="asp-card-head">
              <h2 className="asp-card-title">
                <span className="step-index">1</span> Add products
              </h2>
              <button
                type="button"
                className="asp-refresh-btn"
                onClick={refreshProducts}
                disabled={refreshingProducts}
              >
                <FiRefreshCw className={refreshingProducts ? 'spin' : ''} />
                {refreshingProducts ? 'Refreshing…' : 'Refresh stock'}
              </button>
            </div>

            <div className="asp-search-wrap" ref={searchRef}>
              <input
                type="text"
                id="productSearch"
                name="productSearch"
                className={`asp-search-input ${errors.salesItems ? 'has-error' : ''}`}
                value={currentItem.productSearch}
                onChange={handleCurrentItemChange}
                placeholder="Search product by name or ID…"
                autoComplete="off"
                disabled={isSubmitting}
              />
              {errors.salesItems && <span className="asp-error-message">{errors.salesItems}</span>}

              {currentItem.showDropdown && filteredProducts.length > 0 && (
                <div className="asp-dropdown">
                  {filteredProducts.map(product => {
                    const outOfStock = product.stock <= 0;
                    return (
                      <div
                        key={product.id}
                        className={`asp-option ${currentItem.productId === product.id ? 'selected' : ''} ${outOfStock ? 'out-of-stock' : ''}`}
                        onClick={() => !outOfStock && selectProduct(product)}
                        aria-disabled={outOfStock}
                      >
                        <span className="asp-option-name">{product.name}</span>
                        <span className="asp-option-meta">
                          {outOfStock ? (
                            <span className="asp-stock-warning">0 left in stock</span>
                          ) : (
                            <span>Stock: {product.stock}</span>
                          )}
                          <span>Rs{product.sellingPrice.toLocaleString('en-IN')}/pc</span>
                          {product.unit === 'set' && (
                            <span>Rs{(product.sellingPrice * 6).toLocaleString('en-IN')}/set</span>
                          )}
                          {product.unit === 'dozen' && (
                            <span>Rs{(product.sellingPrice * 12).toLocaleString('en-IN')}/dozen</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {currentItem.productId && (
              <div className="asp-editor">
                <div className="asp-editor-grid">
                  <div className="asp-field">
                    <label htmlFor="unit">Unit</label>
                    <select
                      id="unit"
                      name="unit"
                      value={currentItem.unit}
                      onChange={handleUnitChange}
                      disabled={isSubmitting}
                    >
                      {getUnitOptions(products.find(p => p.id === currentItem.productId)?.unit || 'piece')
                        .map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>

                  <div className="asp-field asp-field-qty">
                    <label htmlFor="quantity">Quantity</label>
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      value={currentItem.quantity}
                      onChange={handleCurrentItemChange}
                      min="1"
                      max={products.find(p => p.id === currentItem.productId)?.stock || 1}
                      onWheel={(e) => e.target.blur()}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="asp-field">
                    <label htmlFor="sellingPrice">Price (Rs)</label>
                    <input
                      type="number"
                      id="sellingPrice"
                      name="sellingPrice"
                      value={currentItem.calculatedPrice}
                      onChange={(e) => {
                        const rawValue = e.target.value;

                        if (rawValue === '') {
                          setCurrentItem(prev => ({
                            ...prev,
                            sellingPrice: '',
                            calculatedPrice: ''
                          }));
                          return;
                        }

                        const newPrice = parseFloat(rawValue);
                        if (isNaN(newPrice)) {
                          setCurrentItem(prev => ({
                            ...prev,
                            calculatedPrice: rawValue
                          }));
                          return;
                        }

                        const basePrice = currentItem.unit === 'set' ? newPrice / 6 :
                                        currentItem.unit === 'dozen' ? newPrice / 12 :
                                        newPrice;

                        setCurrentItem(prev => ({
                          ...prev,
                          sellingPrice: basePrice,
                          calculatedPrice: rawValue
                        }));
                      }}
                      min="0"
                      step="any"
                      onWheel={(e) => e.target.blur()}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="asp-add-btn"
                  onClick={addSalesItem}
                  disabled={isSubmitting || !currentItem.productId || !currentItem.quantity || !currentItem.sellingPrice}
                >
                  <FiPlus /> Add to sale
                </button>
              </div>
            )}
          </div>

          {/* Items in this sale */}
          <div className="asp-card">
            <div className="asp-card-head">
              <h2 className="asp-card-title">
                <span className="step-index">2</span> Items in this sale
              </h2>
            </div>

            {salesItems.length === 0 ? (
              <div className="asp-empty-hint">Search above and add products to start building this sale.</div>
            ) : (
              <div className="asp-items-list">
                {salesItems.map((item, index) => (
                  <div key={index} className="asp-item-row">
                    <div>
                      <div className="asp-item-name">{item.item_name}</div>
                      <div className="asp-item-qty">
                        {item.quantity} {item.unit} × Rs{fmt(normalizedUnitPrice(item))}
                      </div>
                    </div>
                    <div className="asp-item-amount">Rs{fmt(lineTotal(item))}</div>
                    <button
                      type="button"
                      className="asp-item-remove"
                      onClick={() => removeSalesItem(index)}
                      disabled={isSubmitting}
                      title="Remove item"
                    >
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer */}
          <div className="asp-card">
            <div className="asp-card-head">
              <h2 className="asp-card-title">
                <span className="step-index">3</span> Customer
              </h2>
            </div>
            <div className="asp-field-grid">
              <div className="asp-field">
                <label htmlFor="retailerId">Retailer</label>
                <select
                  id="retailerId"
                  name="retailerId"
                  value={formData.retailerId}
                  onChange={handleFormChange}
                  disabled={isSubmitting}
                >
                  <option value="">Walk-in customer</option>
                  {retailers.map(retailer => (
                    <option key={retailer.id} value={retailer.id}>
                      {retailer.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="asp-field">
                <label htmlFor="saleDate">Sale date</label>
                <input
                  type="date"
                  id="saleDate"
                  name="saleDate"
                  value={formData.saleDate}
                  onChange={handleFormChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {!formData.retailerId && calculateTotal() > parseFloat(formData.paidAmount || 0) && (
              <div className={`asp-field ${errors.customerName ? 'has-error' : ''}`} style={{ marginTop: '1rem' }}>
                <label htmlFor="customerName">Customer name *</label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  className={errors.customerName ? 'error-input' : ''}
                  value={formData.customerName}
                  onChange={handleFormChange}
                  placeholder="Who is this credit sale for?"
                  disabled={isSubmitting}
                />
                {errors.customerName && <span className="asp-error-message">{errors.customerName}</span>}
              </div>
            )}
          </div>

          {/* Discount */}
          {salesItems.length > 0 && (
            <div className="asp-card">
              <div className="asp-card-head">
                <h2 className="asp-card-title">
                  <span className="step-index">4</span> Discount
                </h2>
              </div>
              <div className="asp-discount-row">
                <div className="asp-type-toggle">
                  <button
                    type="button"
                    className={formData.discountType === 'amount' ? 'active' : ''}
                    onClick={() => setFormData(prev => ({ ...prev, discountType: 'amount' }))}
                    disabled={isSubmitting}
                  >
                    Rs
                  </button>
                  <button
                    type="button"
                    className={formData.discountType === 'percentage' ? 'active' : ''}
                    onClick={() => setFormData(prev => ({ ...prev, discountType: 'percentage' }))}
                    disabled={isSubmitting}
                  >
                    %
                  </button>
                </div>
                <div className="asp-field asp-discount-value">
                  <input
                    type="number"
                    id="discountValue"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleFormChange}
                    min="0"
                    max={formData.discountType === 'percentage' ? 100 : undefined}
                    step="any"
                    onWheel={(e) => e.target.blur()}
                    disabled={isSubmitting}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment */}
          <div className="asp-card">
            <div className="asp-card-head">
              <h2 className="asp-card-title">
                <span className="step-index">5</span> Payment
              </h2>
            </div>

            <div className="asp-field">
              <label>Payment method</label>
              <div className="asp-payment-methods">
                <button
                  type="button"
                  className={formData.paymentType === 'cash' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, paymentType: 'cash', bankAccountId: '' }))}
                  disabled={isSubmitting}
                >
                  <span className="method-icon">💵</span> Cash
                </button>
                <button
                  type="button"
                  className={formData.paymentType === 'bank' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, paymentType: 'bank' }))}
                  disabled={isSubmitting}
                >
                  <span className="method-icon">🏦</span> Bank
                </button>
                <button
                  type="button"
                  className={formData.paymentType === 'cheque' ? 'active' : ''}
                  onClick={() => setFormData(prev => ({ ...prev, paymentType: 'cheque', bankAccountId: '' }))}
                  disabled={isSubmitting}
                >
                  <span className="method-icon">📋</span> Cheque
                </button>
              </div>
            </div>

            {formData.paymentType === 'bank' && (
              <div className={`asp-field ${errors.bankAccountId ? 'has-error' : ''}`}>
                <label htmlFor="bankAccountId">Transfer to bank account *</label>
                <select
                  id="bankAccountId"
                  name="bankAccountId"
                  value={formData.bankAccountId}
                  onChange={handleFormChange}
                  disabled={isSubmitting || bankAccounts.length === 0}
                  className={errors.bankAccountId ? 'error-input' : ''}
                >
                  <option value="">Select bank account</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_name} - {account.account_number}
                    </option>
                  ))}
                </select>
                {bankAccounts.length === 0 && (
                  <span className="asp-error-message">Create a bank account before recording bank payments.</span>
                )}
                {errors.bankAccountId && <span className="asp-error-message">{errors.bankAccountId}</span>}
              </div>
            )}

            <div className="asp-field-grid">
              <div className="asp-field">
                <label htmlFor="receiptNo">Receipt number</label>
                <input
                  type="text"
                  id="receiptNo"
                  name="receiptNo"
                  value={formData.receiptNo}
                  onChange={handleFormChange}
                  placeholder="Optional"
                  disabled={isSubmitting}
                />
              </div>

              <div className={`asp-field ${errors.paidAmount ? 'has-error' : ''}`}>
                <label htmlFor="paidAmount">Paid amount (Rs) *</label>
                <input
                  type="number"
                  id="paidAmount"
                  name="paidAmount"
                  value={formData.paidAmount}
                  onChange={handleFormChange}
                  step="any"
                  onWheel={(e) => e.target.blur()}
                  max={netTotal}
                  className={errors.paidAmount ? 'error-input' : ''}
                  disabled={isSubmitting}
                />
                {errors.paidAmount && <span className="asp-error-message">{errors.paidAmount}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Receipt rail — signature element, always visible while building the sale */}
        <aside className="asp-receipt-rail">
          <div className="asp-receipt-perf" aria-hidden="true" />
          <div className="asp-receipt-body">
            <div className="asp-receipt-brand">
              <div className="name">Shasha Kitchen</div>
              <div className="tagline">Sale receipt</div>
            </div>

            <hr className="asp-receipt-divider" />

            {salesItems.length === 0 ? (
              <div className="asp-receipt-empty">Nothing added yet</div>
            ) : (
              <div className="asp-receipt-items">
                {salesItems.map((item, index) => (
                  <div key={index} className="asp-receipt-line">
                    <span className="item-label">
                      {item.item_name}
                      <span className="qty">{item.quantity} {item.unit}</span>
                    </span>
                    <span className="item-value">Rs{fmt(lineTotal(item))}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="asp-receipt-totals">
              <div className="asp-receipt-row">
                <span>Subtotal</span>
                <span className="val">Rs{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="asp-receipt-row discount">
                  <span>Discount</span>
                  <span className="val">-Rs{fmt(discount)}</span>
                </div>
              )}
            </div>

            <div className="asp-receipt-total-line">
              <span className="label">Total</span>
              <span className="val">Rs{fmt(netTotal)}</span>
            </div>

            {formData.paidAmount !== '' && (
              <>
                <div className="asp-receipt-paid-row">
                  <span>Paid</span>
                  <span className="val">Rs{fmt(paid)}</span>
                </div>
                {due > 0 && (
                  <div className="asp-receipt-due-row">
                    <span className="label">{formData.retailerId ? 'On credit' : 'Due'}</span>
                    <span className="val">Rs{fmt(due)}</span>
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              className="asp-receipt-submit"
              disabled={isSubmitting || salesItems.length === 0}
            >
              {isSubmitting ? 'Recording sale…' : 'Record sale'} <FiArrowRight />
            </button>
          </div>
        </aside>
      </form>

      {showInvoiceModal && invoicePreview && (
        <div className="invoice-modal asp-invoice-modal">
          <div className="invoice-modal-content">
              <h1>SK</h1>
            <div className="invoice-header">
              <h2>Invoice #{invoicePreview.invoice_no}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowInvoiceModal(false);
                  navigate('/sales');
                }}
              >
                <FiX />
              </button>
            </div>

            <div className="invoice-details">
              <div className="invoice-meta">
                <div>
                  <strong>Date:</strong>
                  {new Date(invoicePreview.sale_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {invoicePreview.retailer_name && (
                  <div>
                    <strong>Customer:</strong> {invoicePreview.retailer_name}
                  </div>
                )}
                {invoicePreview.customer_name && !invoicePreview.retailer_name && (
                  <div>
                    <strong>Customer:</strong> {invoicePreview.customer_name}
                  </div>
                )}
              </div>

              <table className="invoice-items">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                 {invoicePreview?.items?.map((item, index) => (
  <tr key={index}>
    <td>{item.item_name}</td>
    <td>{item.quantity} {item.unit}</td>
    <td>Rs{(item.normalized_price || 0).toLocaleString('en-IN')}</td>
    <td>Rs{(item.total_price || 0).toLocaleString('en-IN')}</td>
  </tr>
))}
                </tbody>
                <tfoot>
                  {invoicePreview.subtotal_amount != null && invoicePreview.subtotal_amount !== invoicePreview.total_amount && (
                    <tr>
                      <td colSpan="4" className="text-right"><strong>Subtotal:</strong></td>
                      <td>Rs{invoicePreview.subtotal_amount.toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                  {invoicePreview.discount_amount > 0 && (
                    <tr>
                      <td colSpan="4" className="text-right"><strong>Discount:</strong></td>
                      <td>-Rs{invoicePreview.discount_amount.toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                    <td>Rs{invoicePreview.total_amount.toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="text-right"><strong>Paid Amount:</strong></td>
                    <td>Rs{invoicePreview.paid_amount.toLocaleString('en-IN')}</td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="text-right"><strong>Payment Method:</strong></td>
                    <td style={{textTransform:'capitalize'}}>
                      {invoicePreview.paid_amount <= 0 ?
                        'Credit' :
                        invoicePreview.payment_type === 'bank' ? '🏦 Bank' :
                        invoicePreview.payment_type === 'cheque' ? '📋 Cheque' : '💵 Cash'}
                    </td>
                  </tr>
                  {invoicePreview.total_amount > invoicePreview.paid_amount && (
                    <tr>
                      <td colSpan="4" className="text-right"><strong>Credit:</strong></td>
                      <td>Rs{(invoicePreview.total_amount - invoicePreview.paid_amount).toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>

            <div className="invoice-actions">
              <button
                className="print-btn"
                onClick={() => window.print()}
              >
                Print Invoice
              </button>
              <button
                className="done-btn"
                onClick={() => {
                  setShowInvoiceModal(false);
                  navigate('/sales');
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSales;
